import React, { useRef, useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, TextInput, View } from 'react-native';
import Text from '@/components/Typography/Text';
import { useTheme } from '@/context/ThemeContext';

export default function WorkGoalEditor({ initialObjective = '', onSubmit, onCancel, disabled = false, editing = false }) {
  const { colors } = useTheme();
  const [objective, setObjective] = useState(initialObjective);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const saving = useRef(false);
  const locked = busy || disabled;
  const save = async () => {
    if (locked || saving.current || !objective.trim()) return;
    saving.current = true;
    setBusy(true); setError('');
    try {
      const accepted = await onSubmit(objective.trim());
      if (accepted === false) setError('The goal has not started. Resolve the request, then try again.');
    } catch (err) { setError(err?.message || 'Could not start the goal. Please try again.'); }
    finally { saving.current = false; setBusy(false); }
  };
  return (
    <ScrollView keyboardShouldPersistTaps="always" scrollEnabled={false} style={[styles.container, {
      backgroundColor: colors.solid_color_primary, borderColor: colors.border_color_secondary,
    }]}>
      <Text size="s" weight="semibold">{editing ? 'Edit goal' : 'Set a goal'}</Text>
      <Text size="xs" color="secondary">What should Work keep working toward?</Text>
      <TextInput accessibilityLabel="Goal objective" autoFocus value={objective} onChangeText={setObjective}
        multiline maxLength={8000} editable={!locked}
        placeholder="Describe the outcome and how to verify it."
        placeholderTextColor={colors.text_color_tertiary}
        style={[styles.input, { color: colors.text_color_primary, borderColor: colors.border_color_secondary }]}
        onKeyPress={event => {
          if (event.isComposing || event.nativeEvent?.isComposing) return;
          const key = event.key || event.nativeEvent?.key;
          if (key === 'Escape' && !locked) { event.preventDefault?.(); onCancel(); }
          // React Native Web owns onKeyDown and forwards it to onKeyPress.
          if (Platform.OS === 'web' && key === 'Enter' && (event.metaKey || event.ctrlKey)) {
            event.preventDefault(); void save();
          }
        }}
      />
      <Text size="xs" color="secondary">Work continues across replies until the goal is complete or needs your attention. You can pause anytime.</Text>
      <View style={styles.actions}>
        <Pressable accessibilityRole="button" accessibilityLabel="Cancel goal editing" disabled={locked}
          onPress={onCancel} style={styles.button}><Text size="s" color="secondary">Cancel</Text></Pressable>
        <Pressable accessibilityRole="button" accessibilityLabel={editing ? 'Save and start' : 'Start goal'}
          disabled={locked || !objective.trim()} accessibilityState={{ disabled: locked || !objective.trim(), busy }}
          onPress={save} style={[styles.button, { backgroundColor: colors.solid_color_secondary },
            (locked || !objective.trim()) && styles.disabled]}>
          <Text size="s" weight="medium">{busy ? 'Starting…' : editing ? 'Save and start' : 'Start goal'}</Text>
        </Pressable>
      </View>
      {!!error && <Text size="xs" accessibilityRole="alert">{error}</Text>}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: { padding: 14, marginHorizontal: 8, marginBottom: 8, borderWidth: 1, borderRadius: 14, flexGrow: 0, flexShrink: 0 },
  input: { minHeight: 90, maxHeight: 160, marginVertical: 10, borderWidth: 1, borderRadius: 10, padding: 10, fontSize: 15, textAlignVertical: 'top' },
  actions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 8, marginTop: 10 },
  button: { minHeight: 40, paddingHorizontal: 14, borderRadius: 8, justifyContent: 'center' },
  disabled: { opacity: 0.45 },
});
